'use client';

import {useEffect, useRef} from 'react';
import gsap from 'gsap';

type CommonTransitionProps = {
  color?: string;
  opacity?: number;
  backdropFilter?: string;
  transition?: string; // e.g. "opacity 0.2s ease-in-out"
};

const hexToRgba = (hex: string, opacity: number): string => {
  let cleaned = hex.replace('#', '');
  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const CommonTransition = ({
  color = '#000000',
  opacity = 0.85,
  backdropFilter,
  transition = 'opacity 0.2s ease-in-out',
}: CommonTransitionProps) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const backgroundColor = hexToRgba(color, opacity);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    const tl = gsap.timeline({
      onComplete: () => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('transition:done'));
        }
      },
    });

    tl.set(el, {opacity: 0, scale: 1});
    tl.to(el, {
      opacity: 1,
      scale: 1.03,
      duration: 0.4,
      ease: 'power2.out',
    });
    tl.to(el, {
      opacity: 0,
      scale: 1,
      duration: 0.6,
      ease: 'power2.inOut',
    });

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        pointerEvents: 'none',
        backgroundColor,
        backdropFilter,
        WebkitBackdropFilter: backdropFilter,
        transition,
      }}
    />
  );
};

export default CommonTransition;
